import {DiagramTemplates} from "../../diagram/types/diagramEntity";

export type ChatRole = 'user' | 'assistant';

export interface ChatMessage {
    id: string;
    role: ChatRole;
    content: string;
    timestamp: Date;
    toolResults?: ToolResult[];
    isLoading?: boolean;
}

export interface ToolResult {
    toolName: string;
    success: boolean;
    message: string;
    entityId?: string;
}

export interface CreateEntityParams {
    shape: 'box' | 'sphere' | 'cylinder' | 'cone' | 'plane' | 'person';
    color?: string;
    label?: string;
    position?: { x: number; y: number; z: number };
}

export interface ConnectEntitiesParams {
    from: string;
    to: string;
    color?: string;
    label?: string;
}

export interface RemoveEntityParams {
    target: string;
}

export interface ModifyEntityParams {
    target: string;
    color?: string;
    label?: string;
    shape?: 'box' | 'sphere' | 'cylinder' | 'cone' | 'plane' | 'person';
    position?: { x: number; y: number; z: number };
    scale?: { x: number; y: number; z: number } | number;
    rotation?: { x: number; y: number; z: number } | number;  // in degrees
}

export interface ClearDiagramParams {
    confirmed: boolean;
}

export interface ModifyConnectionParams {
    target?: string;
    from?: string;
    to?: string;
    label?: string;
    color?: string;
}

export interface SetModelParams {
    model_id: string;
}

export interface WikipediaSearchParams {
    topic: string;
}

export interface SetConnectionLabelPreferenceParams {
    use_default_labels: boolean;
}

export type DiagramToolCall =
    | { name: 'create_entity'; input: CreateEntityParams }
    | { name: 'connect_entities'; input: ConnectEntitiesParams }
    | { name: 'remove_entity'; input: RemoveEntityParams }
    | { name: 'modify_entity'; input: ModifyEntityParams }
    | { name: 'modify_connection'; input: ModifyConnectionParams }
    | { name: 'list_entities'; input: Record<string, never> }
    | { name: 'clear_diagram'; input: ClearDiagramParams }
    | { name: 'get_camera_position'; input: Record<string, never> }
    | { name: 'list_models'; input: Record<string, never> }
    | { name: 'get_current_model'; input: Record<string, never> }
    | { name: 'set_model'; input: SetModelParams }
    | { name: 'clear_conversation'; input: Record<string, never> }
    | { name: 'search_wikipedia'; input: WikipediaSearchParams }
    | { name: 'set_connection_label_preference'; input: SetConnectionLabelPreferenceParams }
    | { name: 'get_connection_label_preference'; input: Record<string, never> };

export const SHAPE_TO_TEMPLATE: Record<CreateEntityParams['shape'], DiagramTemplates> = {
    box: DiagramTemplates.BOX,
    sphere: DiagramTemplates.SPHERE,
    cylinder: DiagramTemplates.CYLINDER,
    cone: DiagramTemplates.CONE,
    plane: DiagramTemplates.PLANE,
    person: DiagramTemplates.PERSON,
};

export const COLOR_NAME_TO_HEX: Record<string, string> = {
    red: '#ff0000',
    green: '#00ff00',
    blue: '#0000ff',
    yellow: '#ffff00',
    orange: '#ffa500',
    purple: '#4b0082',
    cyan: '#00ffff',
    pink: '#ff69b4',
    white: '#ffffff',
    black: '#222222',
    brown: '#8b4513',
    gray: '#778899',
    grey: '#778899',
};

// Reverse mapping from hex to color name
export const HEX_TO_COLOR_NAME: Record<string, string> = Object.entries(COLOR_NAME_TO_HEX)
    .reduce((acc, [name, hex]) => {
        acc[hex.toLowerCase()] = name;
        return acc;
    }, {} as Record<string, string>);

// Template to shape name mapping
export const TEMPLATE_TO_SHAPE: Record<string, string> = {
    '#box-template': 'box',
    '#sphere-template': 'sphere',
    '#cylinder-template': 'cylinder',
    '#cone-template': 'cone',
    '#plane-template': 'plane',
    '#person-template': 'person',
};

// Session types
export interface SessionEntity {
    id: string;
    template: string;
    text?: string;
    color?: string;
    position?: { x: number; y: number; z: number };
    scale?: { x: number; y: number; z: number };
}

export interface SessionMessage {
    role: ChatRole;
    content: string;
    toolResults?: ToolResult[];
    timestamp: Date;
}

export interface DiagramSession {
    id: string;
    diagramId: string;
    conversationHistory: SessionMessage[];
    entities: SessionEntity[];
    createdAt: Date;
    lastAccess: Date;
}

export interface CreateSessionResponse {
    session: DiagramSession;
    isNew: boolean;
}

export interface SyncEntitiesResponse {
    success: boolean;
    entityCount: number;
}

export interface UsageRequestDetail {
    timestamp: number;
    model: string;
    inputTokens: number;
    outputTokens: number;
    cacheCreationTokens: number;
    cacheReadTokens: number;
    cost: number;
    inputText: string | null;
    outputText: string | null;
    toolCalls: Array<{ name: string; input: Record<string, unknown> }>;
}

export interface SessionUsage {
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCacheCreationTokens: number;
    totalCacheReadTokens: number;
    totalCost: number;
    totalCostFormatted: string;
    requestCount: number;
    startTime: number;
    requests?: UsageRequestDetail[];
    // Context tracking
    contextUsed?: number;
    contextLimit?: number;
    contextPercent?: number;
}
